import {
  KeyboardEvent,
  ReactElement,
  SyntheticEvent,
  useRef,
  useState,
} from 'react'
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MuxPlayer from '@mux/mux-player-react'
import type MuxPlayerElement from '@mux/mux-player'

import { QuestionAnswer, QuizQuestionV2 } from 'src/v3/core/types'
import {
  CloseIcon,
  CorrectIcon,
  EnlargeIcon,
  IncorrectIcon,
  RightArrowIcon,
} from 'src/v3/core/icons'
import {
  onQuestionImageError,
  questionImageSrc,
} from 'src/v3/components/quiz/utils/question-image'

// Purely presentational. This component fetches nothing, owns no session, and
// decides nothing about progression — callers hold that state (classroom-v2 in
// its wizard slice, events in a local-state adapter) and pass down what to
// render. It was previously doing its own axios calls out of local useState,
// which meant compliance-relevant state lived in a component; see
// classroom-v2/components/quiz-step.tsx for where that now lives.
export type QuizAnswerFeedback = {
  isCorrect: boolean
  // Sent by both backends, intentionally unused for highlighting — see
  // choiceState. Kept so the shape still matches what callers receive.
  correctAnswer: string
  explanation: string
}

// Off-screen but still announced. Same technique as @mui/utils' visuallyHidden,
// inlined because that package is only a transitive dependency here and this is
// the one place that needs it. display:none or visibility:hidden would remove it
// from the accessibility tree, which is the opposite of the intent.
let visuallyHidden = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const

// Question media is frequently the question itself — an ECG strip, a waveform, a
// scene photo — so a thumbnail beside the text can make an answerable question
// unanswerable. Both media types get a real enlarge affordance, by different
// means, because the right mechanism differs:
//
//   Image: a lightbox dialog. Nothing is lost by mounting a second copy at full
//          size, and a dialog can go far larger than the column allows.
//   Video: the player's OWN fullscreen, requested on the live element. Mounting
//          a second MuxPlayer in a dialog would restart playback from zero and
//          lose the user's position — for a 90-second procedure clip they're
//          scrubbing through, that's worse than the small size.
//
// The affordance is always rendered rather than shown on hover: hover-only
// controls are invisible on touch, which is where the small-media problem is
// worst.
let overlayControlSx = {
  position: 'absolute',
  top: 8,
  right: 8,
  bgcolor: alpha('#000', 0.6),
  color: 'common.white',
  '&:hover': { bgcolor: alpha('#000', 0.8) },
} as const

// Used for the video only. The image's affordance is the image itself (see
// QuestionImage) — there, a second control pointing at the same action would be
// a second tab stop with nothing of its own to do.
let EnlargeButton = ({
  label,
  onClick,
}: {
  label: string
  onClick: () => void
}): ReactElement => (
  <Tooltip title={label}>
    <IconButton
      onClick={onClick}
      aria-label={label}
      size="small"
      sx={overlayControlSx}
    >
      <EnlargeIcon fontSize="small" />
    </IconButton>
  </Tooltip>
)

let QuestionImage = ({
  imageName,
  questionNumber,
}: {
  imageName: string
  questionNumber: number
}): ReactElement => {
  let [isEnlarged, setIsEnlarged] = useState(false)
  let label = `Question ${questionNumber} image`
  let src = questionImageSrc(imageName)

  // The whole image IS the control, as a real <button>, rather than an img with
  // an onClick plus a separate icon button. Click-to-zoom on the image is the
  // behaviour people expect, and a bare img with a click handler is reachable
  // only by mouse — no tab stop, no Enter/Space, no name — which for media that
  // often IS the question means a keyboard or screen reader user can't get to a
  // legible copy of the thing they're being asked about. Delegating to <button>
  // rather than adding tabIndex/role/onKeyDown gets focus, both key activations,
  // and the accessible name from one element that already has them.
  //
  // The icon is decorative here (aria-hidden) — the button's own label names the
  // action, so announcing the icon too would just repeat it. The inner img stays
  // alt="" for the same reason: the question text carries the meaning, an
  // invented description would be worse than none, and the button is what needs
  // the name. In the lightbox the image is the subject of the dialog, so it gets
  // named there instead.
  return (
    <>
      <Box sx={{ position: 'relative', width: '100%' }}>
        <Box
          component="button"
          type="button"
          aria-label={`${label} — select to enlarge`}
          onClick={() => setIsEnlarged(true)}
          sx={{
            display: 'block',
            width: '100%',
            p: 0,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: 'transparent',
            cursor: 'zoom-in',
            font: 'inherit',
            color: 'inherit',
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          <Box
            component="img"
            alt=""
            src={src}
            onError={(event: SyntheticEvent<HTMLElement, Event>) =>
              onQuestionImageError(event, imageName)
            }
            sx={{ display: 'block', width: '100%' }}
          />
        </Box>
        {/* No Tooltip: it can't fire on a pointerEvents:none element, and
            wrapping the button instead would put a redundant tooltip on the
            whole image. The button's aria-label already says what activating it
            does, and the zoom-in cursor says it visually. */}
        <EnlargeIcon
          aria-hidden
          fontSize="small"
          sx={{
            ...overlayControlSx,
            // Never intercept a click meant for the button underneath — this is
            // a hint about that button, not a control of its own.
            pointerEvents: 'none',
            borderRadius: '50%',
            p: 0.75,
            boxSizing: 'content-box',
          }}
        />
      </Box>
      <Dialog
        open={isEnlarged}
        onClose={() => setIsEnlarged(false)}
        maxWidth="lg"
        fullWidth
        aria-label={label}
      >
        <DialogContent
          sx={{ position: 'relative', p: 0, bgcolor: 'common.black' }}
        >
          <IconButton
            onClick={() => setIsEnlarged(false)}
            aria-label="Close enlarged image"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: alpha('#000', 0.6),
              color: 'common.white',
              '&:hover': { bgcolor: alpha('#000', 0.8) },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            alt={label}
            src={src}
            onError={(event: SyntheticEvent<HTMLElement, Event>) =>
              onQuestionImageError(event, imageName)
            }
            sx={{
              display: 'block',
              width: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}

let QuestionVideo = ({
  src,
  questionID,
  questionNumber,
  userID,
}: {
  src: string
  questionID: string
  questionNumber: number
  userID?: string
}): ReactElement => {
  let playerRef = useRef<MuxPlayerElement | null>(null)
  let label = `Question ${questionNumber} video`

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <MuxPlayer
        title={label}
        ref={playerRef}
        src={src}
        accentColor="#f9ea4f"
        primaryColor="#6694E9"
        backwardSeekOffset={20}
        forwardSeekOffset={20}
        metadata={{
          video_id: questionID,
          video_title: label,
          viewer_user_id: userID,
        }}
        defaultHiddenCaptions={true}
        streamType="on-demand"
      />
      {/* Fullscreen on the element that's already playing, so position and
          playback state survive the expand. The player ships its own fullscreen
          control too; this duplicates it deliberately, in the same spot as the
          image's button, because the in-player control is small and easy to miss
          at this size — which is the complaint being fixed. */}
      <EnlargeButton
        label="Expand video to fullscreen"
        onClick={() => playerRef.current?.requestFullscreen()}
      />
    </Box>
  )
}

type ChoiceState = 'idle' | 'selected' | 'correct' | 'incorrect' | 'muted'

let choiceState = (
  answerID: string,
  selectedAnswerID: string | null,
  feedback: QuizAnswerFeedback | null,
): ChoiceState => {
  if (!feedback) return selectedAnswerID === answerID ? 'selected' : 'idle'
  // The only choice highlighted after answering is the one the user actually
  // picked — right or wrong. A wrong answer deliberately does NOT light up the
  // correct one: pointing at the right box teaches nothing, and it lets the user
  // skip the explanation, which is where the educator does the work of getting
  // them there. Every other choice recedes so attention lands on the panel.
  if (answerID !== selectedAnswerID) return 'muted'
  return feedback.isCorrect ? 'correct' : 'incorrect'
}

// Exactly one choice can be picked, so these are radios rather than toggle
// buttons: aria-pressed would tell assistive tech each choice is an independent
// on/off control, which misdescribes both the constraint and what picking a
// second one does to the first.
//
// role="radio" only carries that meaning inside a role="radiogroup" parent (see
// AnswerChoiceGroup), which also owns the arrow-key navigation and roving
// tabindex the radio pattern requires — a focusable radio that doesn't respond
// to arrows is its own failure. Native <input type="radio"> would supply all of
// this for free, but can't be styled into these cards; the tradeoff is that the
// behaviour has to be implemented here instead.
let AnswerChoice = ({
  answer,
  state,
  isSelected,
  isFocusTarget,
  onSelect,
  onKeyDown,
  registerRef,
}: {
  answer: QuestionAnswer
  state: ChoiceState
  isSelected: boolean
  isFocusTarget: boolean
  onSelect?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void
  registerRef: (element: HTMLElement | null) => void
}): ReactElement => {
  let theme = useTheme()
  let isInteractive = onSelect !== undefined

  let accent =
    state === 'correct'
      ? theme.palette.success.main
      : state === 'incorrect'
        ? theme.palette.warning.main
        : state === 'selected'
          ? theme.palette.primary.main
          : null

  return (
    <Box
      // Still a real <button> so Enter/Space activate it without extra
      // handlers; role overrides only what it's announced as.
      component="button"
      type="button"
      ref={registerRef}
      role="radio"
      aria-checked={isSelected}
      // Once an answer is recorded the group becomes a readout. Disabled rather
      // than removed from the tab order, so a screen reader user can still
      // review every choice against the explanation.
      aria-disabled={!isInteractive}
      // Roving tabindex: the group is ONE tab stop, and arrows move within it.
      tabIndex={isFocusTarget ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      // Suppresses the browser's default click-to-focus step WITHOUT touching
      // the click itself (mousedown and click are separate events; the click
      // still fires and still selects). This is the actual fix for arrow keys
      // silently restaging an answer: without it, a mouse click leaves this
      // button holding DOM focus on Windows/Linux (not macOS, which doesn't
      // focus a button on click by default — why the bug was per-tester), and
      // the very next arrow key — pressed to scroll the page, not to change
      // the answer — gets delivered here instead, moving focus AND selection
      // per the ARIA radio pattern above. With this, a mouse-only test-taker's
      // click never leaves the radiogroup holding focus at all, so that arrow
      // key falls through to the page as an ordinary scroll. A keyboard user
      // who tabs in deliberately is unaffected — nothing here touches Tab.
      onMouseDown={(event) => event.preventDefault()}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        px: 2,
        py: 1.75,
        font: 'inherit',
        textAlign: 'left',
        color: 'inherit',
        borderRadius: 2,
        border: '1px solid',
        borderColor: accent ?? 'divider',
        bgcolor: accent
          ? alpha(accent, state === 'selected' ? 0.12 : 0.16)
          : 'background.paperDark',
        opacity: state === 'muted' ? 0.5 : 1,
        cursor: isInteractive ? 'pointer' : 'default',
        '&:hover': isInteractive
          ? { borderColor: theme.palette.primary.main }
          : {},
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.common.white}`,
          outlineOffset: 2,
        },
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {state === 'correct' ? (
        <CorrectIcon sx={{ fontSize: 24, color: 'success.main' }} />
      ) : state === 'incorrect' ? (
        <IncorrectIcon sx={{ fontSize: 24, color: 'warning.main' }} />
      ) : (
        // The selection indicator, sized to match the correct/incorrect icons
        // that replace it once an answer is scored so the text doesn't shift.
        // aria-hidden: aria-checked on the button already announces selection,
        // and this only restates it in colour.
        <Box
          aria-hidden
          sx={{
            width: 24,
            height: 24,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor:
              state === 'selected'
                ? 'primary.main'
                : alpha(theme.palette.common.white, 0.08),
          }}
        />
      )}
      <Typography
        variant="body1"
        sx={{
          color:
            state === 'correct'
              ? 'success.main'
              : state === 'incorrect'
                ? 'warning.main'
                : 'text.primary',
        }}
      >
        {answer.text}
      </Typography>
      {/* Correct/incorrect is conveyed by colour and icon, neither of which a
          screen reader announces — so it's stated in text too. Visually hidden
          rather than aria-label on the button, which would replace the answer
          text instead of adding to it. */}
      {(state === 'correct' || state === 'incorrect') && (
        <Box component="span" sx={visuallyHidden}>
          {state === 'correct'
            ? '(your answer, correct)'
            : '(your answer, incorrect)'}
        </Box>
      )}
    </Box>
  )
}

// Owns the radio-group behaviour the role requires: one tab stop for the whole
// group, arrows to move between choices, and selection following focus the way
// a native radio group does — the APG radio pattern requires arrows to both
// move focus AND select, and a screen reader user is entitled to expect that,
// so this does NOT decouple them despite what caused the bug below.
//
// Arrows wrap at both ends, per the WAI-ARIA radio pattern. Once feedback is in
// nothing is interactive, so arrows only move focus and select nothing — a
// scored answer can't be changed by keyboard any more than by mouse.
//
// The actual bug select-follows-focus exposed wasn't arrow-to-select itself —
// it was a mouse click leaving its <button> holding DOM focus (true on
// Windows/Linux; macOS does not focus a button on click by default, which is
// why this surfaced as per-tester, per-OS score corruption rather than a
// consistent one). With focus left sitting there, a SINGLE arrow key pressed
// afterwards — to scroll the page, not to change the answer — silently
// restaged a neighbouring choice via this same select-follows-focus behaviour,
// and preventDefault() below suppressed the scroll that would have been the
// only clue. Fixed at the source instead, on each AnswerChoice's onMouseDown:
// preventing the mousedown's default stops the browser's click-to-focus step
// without touching the click itself, so a mouse-only test-taker's click never
// leaves this radiogroup holding focus at all, and a keyboard user tabbing in
// still gets normal, ARIA-conformant arrow behaviour.
let AnswerChoiceGroup = ({
  answers,
  selectedAnswerID,
  feedback,
  isInteractive,
  onSelectAnswer,
}: {
  answers: QuestionAnswer[]
  selectedAnswerID: string | null
  feedback: QuizAnswerFeedback | null
  isInteractive: boolean
  onSelectAnswer: (answerID: string) => void
}): ReactElement => {
  let choiceRefs = useRef<(HTMLElement | null)[]>([])

  // Which choice the single tab stop lands on: the selected one, else the first.
  // Never -1, so the group is always reachable by keyboard.
  let selectedIndex = answers.findIndex((a) => a.id === selectedAnswerID)
  let focusIndex = selectedIndex === -1 ? 0 : selectedIndex

  let moveFocus = (fromIndex: number, delta: number) => {
    let nextIndex = (fromIndex + delta + answers.length) % answers.length
    choiceRefs.current[nextIndex]?.focus()
    // Selection follows focus while the question is still open, matching native
    // radio behaviour. After feedback, focus moves without selecting.
    if (isInteractive) onSelectAnswer(answers[nextIndex].id)
  }

  let handleKeyDown =
    (index: number) => (event: KeyboardEvent<HTMLElement>) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault()
        moveFocus(index, 1)
        return
      }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault()
        moveFocus(index, -1)
      }
    }

  return (
    <Box
      role="radiogroup"
      aria-label="Answer choices"
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        gap: 1.5,
      }}
    >
      {answers.map((answer, index) => (
        <AnswerChoice
          key={answer.id}
          answer={answer}
          state={choiceState(answer.id, selectedAnswerID, feedback)}
          isSelected={answer.id === selectedAnswerID}
          isFocusTarget={index === focusIndex}
          registerRef={(element) => {
            // Collecting DOM nodes to call .focus() on is inherently imperative
            // — there's no functional way to hold a live element reference.
            // eslint-disable-next-line fp/no-mutation
            choiceRefs.current[index] = element
          }}
          // Re-selecting is free right up until Submit. After that the choices
          // become a readout: the answer is recorded and scored, so there's
          // nothing left to change.
          onSelect={isInteractive ? () => onSelectAnswer(answer.id) : undefined}
          onKeyDown={handleKeyDown(index)}
        />
      ))}
    </Box>
  )
}

// The dot track under the choices. Answered questions are filled, the current
// one is accented by whether the just-submitted answer was right, and unreached
// ones stay grey. Not clickable: which question you're on is the server's call
// (it locks each question as it's displayed), not a thing to jump around in.
let QuestionDots = ({
  total,
  currentIndex,
  feedback,
}: {
  total: number
  currentIndex: number
  feedback: QuizAnswerFeedback | null
}): ReactElement => {
  let theme = useTheme()
  return (
    <Box
      aria-hidden
      sx={{ display: 'flex', justifyContent: 'center', gap: 0.75, py: 2 }}
    >
      {Array.from({ length: total }, (_, i) => (
        <Box
          key={i}
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor:
              i === currentIndex && feedback
                ? feedback.isCorrect
                  ? theme.palette.success.main
                  : theme.palette.warning.main
                : i <= currentIndex
                  ? theme.palette.primary.main
                  : theme.palette.action.disabled,
          }}
        />
      ))}
    </Box>
  )
}

let FeedbackPanel = ({
  feedback,
}: {
  feedback: QuizAnswerFeedback
}): ReactElement => {
  let theme = useTheme()
  let accent = feedback.isCorrect
    ? theme.palette.success.main
    : theme.palette.warning.main

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        mt: 1,
        p: 2.5,
        borderRadius: 1,
        borderLeft: `3px solid ${accent}`,
        bgcolor: 'background.paperDark',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {feedback.isCorrect ? (
          <CorrectIcon sx={{ fontSize: 20, color: accent }} />
        ) : (
          <IncorrectIcon sx={{ fontSize: 20, color: accent }} />
        )}
        <Typography
          variant="overline"
          sx={{ color: accent, fontWeight: 'fontWeightBold', letterSpacing: 1 }}
        >
          {feedback.isCorrect ? 'Correct' : 'Incorrect'}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {feedback.explanation}
      </Typography>
    </Box>
  )
}

export type QuizTakerProps = {
  question: QuizQuestionV2
  answers: QuestionAnswer[]
  questionNumber: number
  totalQuestions: number
  selectedAnswerID: string | null
  // Present once the answer POST has resolved. Its explanation is the ONLY
  // place the answer key is available — the questions payload withholds it.
  feedback: QuizAnswerFeedback | null
  isSubmitting: boolean
  // The footer CTA's label once an answer is in, because only the caller knows
  // whether advancing means "next question" or "score this attempt" — this
  // component has no concept of a session, so it can't and shouldn't decide.
  advanceLabel: string
  isAdvancing: boolean
  userID?: string
  // Selecting only STAGES a choice — nothing is recorded until onSubmitAnswer.
  // An answer is irreversible once posted (it's scored immediately and the
  // explanation is released), so a mis-tap must be correctable, which means the
  // click that records it has to be a separate deliberate one.
  onSelectAnswer: (answerID: string) => void
  onSubmitAnswer: () => void
  onAdvance: () => void
}

export let QuizTaker = ({
  question,
  answers,
  questionNumber,
  totalQuestions,
  selectedAnswerID,
  feedback,
  isSubmitting,
  advanceLabel,
  isAdvancing,
  userID,
  onSelectAnswer,
  onSubmitAnswer,
  onAdvance,
}: QuizTakerProps): ReactElement => {
  let hasMedia = !!question.imageName || !!question.videoName

  return (
    <Card variant="outlined" sx={{ p: { xs: 2.5, sm: 4 } }}>
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: 1, display: 'block', mb: 2 }}
      >
        Question {questionNumber} of {totalQuestions}
      </Typography>

      {/* Question text above the media, not beside it. Side-by-side capped the
          media at a fraction of the card so it could never be more than a
          thumbnail; stacking lets it use the full width, which is what makes an
          ECG strip or waveform legible without expanding at all. */}
      <Typography
        variant="h2"
        sx={{ fontSize: '1.4rem', lineHeight: 1.4, mb: hasMedia ? 2.5 : 3 }}
      >
        {question.text}
      </Typography>

      {hasMedia && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            mb: 3,
            // Wide enough to read, capped so the answers stay above the fold on
            // a laptop — the enlarge affordance covers anything needing more.
            maxWidth: 560,
            mx: 'auto',
          }}
        >
          {question.imageName && (
            <QuestionImage
              imageName={question.imageName}
              questionNumber={questionNumber}
            />
          )}
          {question.videoName && (
            <QuestionVideo
              src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${question.videoName.replace('mp4', 'm3u8')}`}
              questionID={question.id}
              questionNumber={questionNumber}
              userID={userID}
            />
          )}
        </Box>
      )}

      <AnswerChoiceGroup
        answers={answers}
        selectedAnswerID={selectedAnswerID}
        feedback={feedback}
        // Disabling mid-submit keeps a double-tap (or an arrow key) from staging
        // a different answer than the one already in flight.
        isInteractive={!feedback && !isSubmitting}
        onSelectAnswer={onSelectAnswer}
      />

      <QuestionDots
        total={totalQuestions}
        currentIndex={questionNumber - 1}
        feedback={feedback}
      />

      {feedback && <FeedbackPanel feedback={feedback} />}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          mt: 3,
        }}
      >
        {(isSubmitting || isAdvancing) && <CircularProgress size={20} />}
        {feedback ? (
          <Button
            variant="contained"
            endIcon={<RightArrowIcon />}
            disabled={isAdvancing}
            onClick={onAdvance}
          >
            {advanceLabel}
          </Button>
        ) : (
          <Button
            variant="contained"
            // Nothing staged means nothing to record — and the caller can't
            // recover from an empty answerID, so this is the guard, not just a
            // hint.
            disabled={!selectedAnswerID || isSubmitting}
            onClick={onSubmitAnswer}
          >
            Submit Answer
          </Button>
        )}
      </Box>
    </Card>
  )
}
